import MotionScroll from 'motion-scroll'
import { easeInOutCubic } from 'es6-easing'
import store from '@/config/vuex'
import { wait } from '@/helpers/animation'
import { isInViewport } from '@/helpers/intersection-observer'

export function toggleDocumentOverflow(hasOverflow) {
  if (!document.documentElement.matches('.has-overflow-hidden')) {
    store.commit('page/updateScrollBarWidth')
  }
  const action = hasOverflow ? 'remove' : 'add'
  document.documentElement.classList[action]('has-overflow-hidden')
  equalizeScrollbarWidth(!hasOverflow)
  store.commit(
    'page/setScrollbarEqualizerMargin',
    !hasOverflow ? `${store.state.page.scrollbarWidth}px` : ''
  )
}

export function equalizeScrollbarWidth(equalize) {
  const styleValue = equalize ? `margin-right: ${store.state.page.scrollbarWidth}px` : ''
  document.body.setAttribute('style', styleValue)
}

export function scrollToTop() {
  document.body.scrollTop = document.documentElement.scrollTop = 0
}

export const SCROLL_ALIGN_OPTIONS = {
  top: 'bottom',
  bottom: 'bottom',
}

export function scrollSmoothlyToElement(element, params = {}) {
  const offset = params.offset || 0
  const includeHeader = params.includeHeader !== undefined ? includeHeader : true
  const align = params.align || 'top'
  ;['offset', 'includeHeader', 'align'].forEach(prop => delete params[prop])

  const headerHeight = includeHeader ? document.querySelector('header')?.offsetHeight || 0 : 0
  const elBoundingRect = element.getBoundingClientRect()

  let targetPosition
  if (align === SCROLL_ALIGN_OPTIONS.bottom) {
    const innerHeight = window.innerHeight || document.documentElement.clientHeight
    targetPosition =
      window.pageYOffset -
      (innerHeight - elBoundingRect.top) +
      elBoundingRect.height +
      headerHeight +
      offset
  } else {
    // top
    targetPosition = window.pageYOffset + elBoundingRect.top - headerHeight + offset
  }
  if (!params.scrollUpOnly || window.pageYOffset > targetPosition) {
    On.smoothScrollTo(targetPosition, params)
  }
}

export async function scrollSmoothlyToBottom(currentTarget, params = {}) {
  const offset = params.offset || 0
  const initialWaitTime = params.initialWaitTime || 0
  if (initialWaitTime) {
    await wait(initialWaitTime) // used to wait for the keyboard to pop up on mobile
  }
  ['initialWaitTime'].forEach(prop => delete params[prop])
  if (currentTarget && !isInViewport(currentTarget, { bottom: -offset })) {
    scrollSmoothlyToElement(currentTarget, {
      ...params,
      align: SCROLL_ALIGN_OPTIONS.bottom,
    })
  }
}

export function scrollElementTo(element, scrollTo, options = {}) {
  if (!element) return

  const defaultOptions = {
    speed: 600, // pixels per second
    easing: easeInOutCubic,
    minScrollTime: On.TransitionTimes.slow / 1000,
    callBack: null,
  }
  const forcedOptions = { element, scrollTo, force: true }
  MotionScroll.scroll({ ...defaultOptions, ...options, ...forcedOptions })
}

export function createScrollDetector(onScrollStart, onScrollEnd, scrollStopThreshhold = 200) {
  if (!onScrollStart || typeof onScrollStart !== 'function')
    throw new TypeError('Missing prameter "onScrollStart"')

  if (!onScrollEnd || typeof onScrollEnd !== 'function')
    throw new TypeError('Missing prameter "onScrollEnd"')

  const ScrollDetector = function(onScrollStart, onScrollEnd, scrollStopThreshhold) {
    this.timeoutId = undefined

    this.onScrollStart = () => {
      onScrollStart()
    }

    this.onScrollEnd = () => {
      clearTimeout(this.timeoutId)
      this.timeoutId = undefined
      onScrollEnd()
    }

    this.onScroll = () => {
      !this.timeoutId ? this.onScrollStart() : clearTimeout(this.timeoutId)
      this.timeoutId = setTimeout(this.onScrollEnd, scrollStopThreshhold)
    }
  }

  ScrollDetector.prototype.create = function() {
    window.addEventListener('scroll', this.onScroll, false)
  }

  ScrollDetector.prototype.destroy = function() {
    window.removeEventListener('scroll', this.onScroll, false)
    this.onScrollEnd()
  }

  const scrollDetector = new ScrollDetector(onScrollStart, onScrollEnd, scrollStopThreshhold)
  scrollDetector.create()

  return scrollDetector
}

export function observeStickyPosition(stickyEl, callback) {
  if (!window.IntersectionObserver || !stickyEl) return
  const observer = new IntersectionObserver(callback, { threshold: [1] })
  return observer.observe(stickyEl)
}
